import * as yup from 'yup'
import { RefundDestinationType, RefundPaymentRail } from 'src/constants/payments-ops/enums'
import { isRefundAddressRequired } from 'src/utils/paymentsOps/refundExecute'
import { isValidRoutingNumber } from 'src/utils/paymentsOps/routingNumber'

const ACCOUNT_NUMBER_PATTERN = /^\d{6,17}$/

const isCustomerAccount = (destinationType: unknown): boolean => {
  return destinationType === RefundDestinationType.CustomerAccount
}

const needsAddress = (destinationType: unknown, paymentRail: unknown): boolean => {
  return isRefundAddressRequired(
    destinationType as RefundDestinationType,
    paymentRail as RefundPaymentRail,
  )
}

const ZIP_CODE_PATTERN = /^\d{5}(-\d{4})?$/

/** Lengths the RTP address accepts; checked on the trimmed value that is sent. */
export const REFUND_ADDRESS_MAX_LENGTHS = {
  addressLine1: 70,
  addressLine2: 70,
  city: 35,
} as const

const trimmedLength = (value: string | undefined): number => {
  return (value ?? '').trim().length
}

/** Only checked for a customer account over RTP — otherwise the address is hidden and not sent. */
const addressField = (buildSchema: (schema: yup.StringSchema) => yup.StringSchema) => {
  return yup.string().when(['destinationType', 'paymentRail'], {
    is: needsAddress,
    then: buildSchema,
  })
}

const requiredText = (schema: yup.StringSchema, message: string): yup.StringSchema => {
  return schema.test('required', message, (value) => {
    return trimmedLength(value) > 0
  })
}

const maxText = (schema: yup.StringSchema, label: string, maxLength: number): yup.StringSchema => {
  return schema.test('max', `${label} must be at most ${maxLength} characters`, (value) => {
    return trimmedLength(value) <= maxLength
  })
}

export const executeRefundValidator = yup.object({
  destinationType: yup.string().test('known-destination', 'Choose a destination', (value) => {
    return Object.values(RefundDestinationType).includes(value as RefundDestinationType)
  }),
  paymentRail: yup.string().test('known-rail', 'Choose a payment rail', (value) => {
    return Object.values(RefundPaymentRail).includes(value as RefundPaymentRail)
  }),
  recipientName: yup.string().when('destinationType', {
    is: isCustomerAccount,
    then: (schema) => {
      return schema.test('required', 'Recipient name is required', (value) => {
        return (value ?? '').trim().length > 0
      })
    },
  }),
  routingNumber: yup.string().when('destinationType', {
    is: isCustomerAccount,
    then: (schema) => {
      return schema.test('aba', 'Routing number is invalid', (value) => {
        return isValidRoutingNumber((value ?? '').trim())
      })
    },
  }),
  accountNumber: yup.string().when('destinationType', {
    is: isCustomerAccount,
    then: (schema) => {
      return schema.test('account', 'Account number must be 6–17 digits', (value) => {
        return ACCOUNT_NUMBER_PATTERN.test((value ?? '').trim())
      })
    },
  }),
  addressLine1: addressField((schema) => {
    return maxText(
      requiredText(schema, 'Address line 1 is required'),
      'Address line 1',
      REFUND_ADDRESS_MAX_LENGTHS.addressLine1,
    )
  }),
  addressLine2: addressField((schema) => {
    return maxText(schema, 'Address line 2', REFUND_ADDRESS_MAX_LENGTHS.addressLine2)
  }),
  city: addressField((schema) => {
    return maxText(
      requiredText(schema, 'City is required'),
      'City',
      REFUND_ADDRESS_MAX_LENGTHS.city,
    )
  }),
  state: addressField((schema) => {
    return requiredText(schema, 'State is required')
  }),
  zipCode: addressField((schema) => {
    return requiredText(schema, 'ZIP is required').test(
      'zip',
      'Enter a 5-digit ZIP or ZIP+4',
      (value) => {
        const trimmed = (value ?? '').trim()

        return trimmed === '' || ZIP_CODE_PATTERN.test(trimmed)
      },
    )
  }),
})
